import React from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Text from '../Text';
import SectionHeader from '../SectionHeader';
import ScrollReveal from '../animated/ScrollReveal';
import MagneticButton from '../animated/MagneticButton';
import TestimonialCarousel from '../animated/TestimonialCarousel';
import { colors, gradients } from '../../theme/designTokens';
import { clients } from '../../data/insights';
import { testimonials } from '../../data/testimonials';
import ravisir from '../../assets/images/ravisir.png';
import { fillImageSx } from '../../theme/designTokens';

export const LeadershipTeaser = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, md: 12 }, background: colors.white }}>
      <Box
        sx={{
          maxWidth: 1100,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1.2fr' },
          gap: { xs: 4, md: 6 },
          alignItems: 'center',
        }}
      >
        <ScrollReveal>
          <Box
            sx={{
              height: { xs: 320, md: 420 },
              overflow: 'hidden',
              background: gradients.sectionDark,
            }}
          >
            <Box component="img" src={ravisir} alt="ALEgION Leadership" sx={{ ...fillImageSx, objectPosition: 'top' }} />
          </Box>
        </ScrollReveal>
        <ScrollReveal delay={0.1}>
          <Text variant="eyebrow" sx={{ color: colors.slate, mb: 2, display: 'block' }}>
            Leadership
          </Text>
          <Text variant="h2" color="primary" sx={{ mb: 2 }}>
            Why ALEgION exists.
          </Text>
          <Text variant="body" color="secondary" sx={{ mb: 3, display: 'block' }}>
            Born from decades of enterprise risk and insurance leadership, ALEgION was built to answer a larger question: how do organizations withstand uncertainty—and emerge stronger? We exist to engineer resilience, not merely arrange protection.
          </Text>
          <MagneticButton variant="navy" onClick={() => navigate('/leadership')}>
            Meet the Leadership
          </MagneticButton>
        </ScrollReveal>
      </Box>
    </Box>
  );
};

export const ClientsMarquee = () => {
  const track = [...clients, ...clients];

  return (
    <Box
      sx={{
        py: 4,
        background: colors.navyDark,
        overflow: 'hidden',
        position: 'relative',
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          width: 80,
          zIndex: 2,
          pointerEvents: 'none',
        },
        '&::before': {
          left: 0,
          background: `linear-gradient(to right, ${colors.navyDark}, transparent)`,
        },
        '&::after': {
          right: 0,
          background: `linear-gradient(to left, ${colors.navyDark}, transparent)`,
        },
      }}
    >
      <Text
        variant="eyebrow"
        sx={{
          textAlign: 'center',
          color: colors.ochre,
          mb: 3,
          display: 'block',
        }}
      >
        Organizations that trust ALEgION
      </Text>
      <Box
        sx={{
          display: 'flex',
          width: 'max-content',
          animation: 'marquee 40s linear infinite',
          '@keyframes marquee': {
            '0%': { transform: 'translateX(0)' },
            '100%': { transform: 'translateX(-50%)' },
          },
        }}
      >
        {track.map((name, i) => (
          <Box
            key={`${name}-${i}`}
            sx={{
              px: 4,
              py: 1,
              whiteSpace: 'nowrap',
              color: colors.silver,
              fontSize: '1rem',
              fontWeight: 500,
              letterSpacing: '0.08em',
              opacity: 0.85,
            }}
          >
            {name}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const TestimonialsSection = () => {
  const items = testimonials.map((t) => ({
    quote: t.quote,
    author: t.name,
    role: t.title,
  }));

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, md: 12 }, background: colors.offWhite }}>
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        <ScrollReveal>
          <SectionHeader
            eyebrow="Testimonials"
            title="Executive perspectives."
            highlightWords={['perspectives']}
            subtitle="Not generic praise—voices from leaders who chose resilience over transactions."
          />
        </ScrollReveal>
        <TestimonialCarousel items={items} />
      </Box>
    </Box>
  );
};
